import React, { useRef, useState } from "react";
import { Author, ChangeSet, State } from "@aidkitorg/typesheets/lib/collab";
import type { MergeResult } from "@aidkitorg/typesheets/lib/collabMerge";
import { Button } from "@aidkitorg/component-library";
import { ConflictDisplay, type ConflictDisplayHandle, type ConflictResolution } from "./ConflictDisplay";
import { FacePile } from "../Realtime";

export type MergeBranchModalProps = {
  mergeResult: MergeResult;
  baseState: State;
  incomingState: State;
  parentState?: State;
  baseLabel: string;
  incomingLabel: string;
  /** Display name of the branch being reset (e.g. "default" or a named branch). */
  branchLabel: string;
  /** When true, completing merge runs publish after sync; UI shows "Merge and Publish". */
  publishAfterMerge: boolean;
  author?: Author;
  facePileChannel?: string;
  onMerge: (resolvedEvents: ChangeSet) => Promise<void>;
  onCancel: () => void;
  onResetToPublished?: () => void;
};

export function MergeBranchModal(props: MergeBranchModalProps) {
  const { mergeResult, baseState, incomingState, parentState, baseLabel, incomingLabel, branchLabel, publishAfterMerge, author, facePileChannel, onMerge, onCancel, onResetToPublished } = props;
  const { automergeObjectCount, conflicts } = mergeResult;

  const [resolutions, setResolutions] = useState<Record<string, ConflictResolution>>({});
  const conflictRefs = useRef<Record<string, ConflictDisplayHandle>>({});
  const [isMerging, setIsMerging] = useState(false);

  const conflictKey = (c: { objectId: string, conflictType: string }) => `${c.objectId}:${c.conflictType}`;

  const allResolved = conflicts.every(c => resolutions[conflictKey(c)] !== undefined);
  const resolvedCount = Object.keys(resolutions).length;

  const primaryMergeLabelNoConflicts = isMerging
    ? 'Merging…'
    : publishAfterMerge
      ? 'Merge and Publish'
      : 'Merge';

  const primaryMergeLabelWithConflicts = isMerging
    ? 'Merging…'
    : !allResolved
      ? `Resolve ${conflicts.length - resolvedCount} remaining`
      : publishAfterMerge
        ? 'Merge and Publish'
        : 'Merge';

  const showSyncOnlyNote = !publishAfterMerge && (conflicts.length === 0 || allResolved);

  const handleResolve = (key: string, resolution: ConflictResolution) => {
    setResolutions(prev => ({ ...prev, [key]: resolution }));
  };

  const handleDeselect = (key: string) => {
    setResolutions(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleMerge = async () => {
    // Collect resolved events from each conflict's display.
    // Each conflict produces events with bumped Lamport timestamps.
    const allResolvedEvents: ChangeSet = [];
    for (const key of Object.keys(resolutions)) {
      const events = conflictRefs.current[key]?.getResolvedEvents();
      if (events) allResolvedEvents.push(...events);
    }
    setIsMerging(true);
    try {
      await onMerge(allResolvedEvents);
    } finally {
      setIsMerging(false);
    }
  };

  if (conflicts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 w-full h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Merge Branch</h2>
          {facePileChannel && author?.name && (
            <FacePile name={author.name} channel={facePileChannel} browserTab={author.tabId} />
          )}
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-green-600 text-lg">&#10003;</span>
            <span className="font-medium text-green-800">No conflicts found</span>
          </div>
          <p className="text-sm text-green-700 mt-1">
            {automergeObjectCount} change{automergeObjectCount !== 1 ? 's' : ''} will be auto-merged.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-wrap justify-end gap-2">
            {onResetToPublished && (
              <Button variant="secondary" isDisabled={isMerging} onClick={() => {
                if (confirm(`Discard all changes and reset branch "${branchLabel}" to the published version? You might lose unsaved changes.`)) {
                  onResetToPublished();
                }
              }}>Discard all changes &amp; Reset branch "{branchLabel}" to Published</Button>
            )}
            <Button variant="secondary" onClick={onCancel} isDisabled={isMerging}>Cancel</Button>
            <Button variant="primary" onClick={handleMerge} isDisabled={isMerging}>
              {primaryMergeLabelNoConflicts}
            </Button>
          </div>
          {showSyncOnlyNote && (
            <span className="text-xs text-gray-500">Syncing only, will not publish</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg shadow-lg p-6 w-full h-full flex flex-col overflow-hidden relative">
      {isMerging && (
        <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-lg">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium text-gray-700">Merging…</span>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-bold">Merge Branch</h2>
        {facePileChannel && author?.name && (
          <FacePile name={author.name} channel={facePileChannel} browserTab={author.tabId} />
        )}
      </div>

      {/* Summary bar */}
      <div className="flex flex-wrap items-start gap-3 bg-white rounded-lg p-3 mb-4 border">
        <div className="flex min-w-0 flex-1 flex-wrap gap-x-4 gap-y-1 text-sm">
          <span className="text-green-700">
            {automergeObjectCount} change{automergeObjectCount !== 1 ? 's' : ''} auto-merged
          </span>
          <span className="text-yellow-700">
            {conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''} to resolve
          </span>
          <span className="text-gray-500">
            {resolvedCount}/{conflicts.length} resolved
          </span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex flex-wrap justify-end gap-2">
            {onResetToPublished && (
              <Button variant="secondary" onClick={() => {
                if (confirm(`Discard all changes and reset branch "${branchLabel}" to the published version? You might lose unsaved changes.`)) {
                  onResetToPublished();
                }
              }}>Discard all changes &amp; Reset branch "{branchLabel}" to Published</Button>
            )}
            <Button variant="secondary" onClick={onCancel} isDisabled={isMerging}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleMerge}
              isDisabled={!allResolved || isMerging}
            >
              {primaryMergeLabelWithConflicts}
            </Button>
          </div>
          {showSyncOnlyNote && (
            <span className="text-xs text-gray-500">Syncing only, will not publish</span>
          )}
        </div>
      </div>

      {/* Conflict list */}
      <div className="space-y-4 flex-1 overflow-y-auto">
        {conflicts.map((conflict) => {
          const key = conflictKey(conflict);
          return (
            <ConflictDisplay
              key={key}
              ref={(handle) => {
                if (handle) conflictRefs.current[key] = handle;
                else delete conflictRefs.current[key];
              }}
              conflict={conflict}
              baseState={baseState}
              incomingState={incomingState}
              parentState={parentState}
              baseLabel={baseLabel}
              incomingLabel={incomingLabel}
              author={author}
              resolution={resolutions[key]}
              onResolve={(resolution) => handleResolve(key, resolution)}
              onDeselect={() => handleDeselect(key)}
            />
          );
        })}
      </div>
    </div>
  );
}
